import { useState, useRef, useEffect } from "react";
import { ArrowRight, Calendar, Loader2, ChevronDown, Check } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "motion/react";
import { evaluationcall } from "../../apihandlers";

type IndustryOption = {
  value: string;
  label: string;
};

const INDUSTRY_OPTIONS: IndustryOption[] = [
  { value: "healthcare", label: "Healthcare" },
  { value: "finance", label: "Finance & Banking" },
  { value: "retail", label: "Retail & E-commerce" },
  { value: "technology", label: "Technology & SaaS" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "education", label: "Education" },
  { value: "logistics", label: "Logistics & Supply Chain" },
  { value: "other", label: "Other" },
];

export function BookCall() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState<string>("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleBookCall = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!selectedIndustry) {
      toast.error("Please select an industry");
      return;
    }

    setIsSubmitting(true);
    
    const form = e.target as HTMLFormElement;
    
    try {
      await evaluationcall({
        name: (form.elements.namedItem('name') as HTMLInputElement)?.value,
        email: (form.elements.namedItem('email') as HTMLInputElement)?.value,
        industry: selectedIndustry,
      });
      setIsSubmitting(false);
      toast.success("Meeting request submitted successfully!", {
        description: "We'll be in touch shortly to confirm your time slot.",
        duration: 5000,
      });
    } catch (e: any) {
      setIsSubmitting(false);
      console.log(e);
      toast.error("Something went wrong, please try again later!");
    }
        
    // Reset form
    form.reset();
    setSelectedIndustry("");
  };

  const selectedOptionLabel = INDUSTRY_OPTIONS.find(opt => opt.value === selectedIndustry)?.label;

  return (
    <section id="book-call" className="py-24 bg-slate-900 relative overflow-hidden">
      <div className="absolute inset-0 bg-[#FD5750]/5"></div>
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#FD5750]/10 to-transparent"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="bg-[#FD5750] rounded-3xl p-8 md:p-16 flex flex-col md:flex-row items-center justify-between gap-12 shadow-2xl">
          <div className="md:w-3/5 text-white">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-white/90 text-lg mb-8 leading-relaxed max-w-xl">
              Don't let technical debt or poor strategy hold you back. Let's discuss how we can help you achieve your business goals. No commitment, just a conversation.
            </p>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
                <span className="font-medium">Free 30-minute consultation</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
                <span className="font-medium">Technical architecture review</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
                <span className="font-medium">Rough project estimation</span>
              </li>
            </ul>
          </div>
          
          <div className="md:w-2/5 w-full flex justify-center">
            <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-sm">
               <div className="text-center mb-6">
                 <div className="w-16 h-16 bg-slate-100 text-[#FD5750] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Calendar size={32} />
                 </div>
                 <h3 className="text-xl font-bold text-slate-900">Book a Free Call</h3>
                 <p className="text-slate-500 text-sm">Select a time that works for you.</p>
               </div>
               
               <form className="space-y-4" onSubmit={handleBookCall}>
                 <div>
                   <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                   <input 
                     type="text"
                     name="name"
                     required
                     className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#FD5750] focus:border-[#FD5750] outline-none" 
                     placeholder="John Doe" 
                   />
                 </div>
                 <div>
                   <label className="block text-sm font-medium text-slate-700 mb-1">Work Email</label>
                   <input 
                     type="email"
                     name="email"
                     required
                     className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#FD5750] focus:border-[#FD5750] outline-none" 
                     placeholder="john@company.com" 
                   />
                 </div>

                 <div ref={dropdownRef}>
                   <label className="block text-sm font-medium text-slate-700 mb-1">Industry</label>
                   <div className="relative">
                     {/* Hidden input for HTML form validation if needed */}
                     <input type="hidden" name="industry" value={selectedIndustry} required />
                     
                     <button
                       type="button"
                       onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                       className={`w-full px-4 py-2 bg-white border rounded-lg outline-none transition-all flex items-center justify-between text-left ${
                         isDropdownOpen 
                           ? "border-[#FD5750] ring-2 ring-[#FD5750]" 
                           : "border-slate-300 focus:border-[#FD5750] focus:ring-2 focus:ring-[#FD5750]"
                       }`}
                     >
                       <span className={`block truncate ${selectedIndustry ? "text-slate-900" : "text-slate-500"}`}>
                         {selectedIndustry ? selectedOptionLabel : "Select your industry"}
                       </span>
                       <motion.div
                         animate={{ rotate: isDropdownOpen ? 180 : 0 }}
                         transition={{ duration: 0.2 }}
                         className="flex-shrink-0"
                       >
                         <ChevronDown size={20} className="text-slate-500" />
                       </motion.div>
                     </button>

                     <AnimatePresence>
                       {isDropdownOpen && (
                         <motion.div
                           initial={{ opacity: 0, y: -10 }}
                           animate={{ opacity: 1, y: 0 }}
                           exit={{ opacity: 0, y: -10 }}
                           transition={{ duration: 0.2 }}
                           className="absolute top-full left-0 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl z-50 overflow-hidden max-h-60"
                         >
                           <ul className="overflow-y-auto max-h-60 py-1">
                             {INDUSTRY_OPTIONS.map((option) => (
                               <li key={option.value}>
                                 <button
                                   type="button"
                                   onClick={() => {
                                     setSelectedIndustry(option.value);
                                     setIsDropdownOpen(false);
                                   }}
                                   className={`w-full px-4 py-2 text-left hover:bg-slate-50 transition-colors flex items-center justify-between ${
                                     selectedIndustry === option.value ? "bg-slate-50 text-[#FD5750] font-medium" : "text-slate-700"
                                   }`}
                                 >
                                   <span className="truncate">{option.label}</span>
                                   {selectedIndustry === option.value && (
                                     <Check size={16} className="text-[#FD5750] flex-shrink-0 ml-2" />
                                   )}
                                 </button>
                               </li>
                             ))}
                           </ul>
                         </motion.div>
                       )}
                     </AnimatePresence>
                   </div>
                 </div>

                 <button 
                   type="submit"
                   disabled={isSubmitting}
                   className="w-full py-3 bg-slate-900 text-white font-bold rounded-lg hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                 >
                   {isSubmitting ? (
                     <>
                       <Loader2 size={18} className="animate-spin" /> Scheduling...
                     </>
                   ) : (
                     <>
                       Schedule Meeting <ArrowRight size={18} />
                     </>
                   )}
                 </button>
               </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
